import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Film, Plus, ArrowRight, CheckCircle2, DownloadCloud, Loader2, Archive, RotateCcw } from "lucide-react";
import DownloadVideoButton from "@/components/DownloadVideoButton";
import ShareToSocial from "@/components/ShareToSocial";

const STATUS_COLORS = {
  draft: "text-zinc-400 bg-white/5",
  voiced: "text-blue-400 bg-blue-500/10",
  scheduled: "text-green-400 bg-green-500/10",
};

export default function Library() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulk, setBulk] = useState({ active: false, done: 0, total: 0 });
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (showArchived) {
      api.get("/videos", { params: { archived: 1 } }).then((r) => setArchived(r.data)).catch(() => {});
    }
  }, [showArchived]);

  const restoreVideo = async (id) => {
    try {
      await api.post(`/videos/${id}/archive?undo=1`);
      setArchived((a) => a.filter((v) => v.id !== id));
      api.get("/videos").then((r) => setVideos(r.data));
      toast.success("Restored to your library.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Live-refresh while any video is rendering so its badge flips to "Ready" automatically.
  useEffect(() => {
    if (!videos.some((v) => v.render_status === "processing")) return;
    const t = setInterval(() => {
      api.get("/videos").then((r) => setVideos(r.data)).catch(() => { /* transient poll error — retried on the next tick */ });
    }, 5000);
    return () => clearInterval(t);
  }, [videos]);

  const readyVideos = videos.filter((v) => v.render_ready);

  const downloadOne = async (v) => {
    const { data: mt } = await api.get(`/videos/${v.id}/media-token`);
    const resp = await api.get(`/videos/${v.id}/download`, { params: { token: mt.token }, responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `videoileo-${v.id}.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  };

  const downloadAll = async () => {
    if (!readyVideos.length || bulk.active) return;
    setBulk({ active: true, done: 0, total: readyVideos.length });
    let ok = 0;
    let failed = 0;
    for (const v of readyVideos) {
      try {
        await downloadOne(v);
        ok += 1;
        setBulk({ active: true, done: ok, total: readyVideos.length });
      } catch (e) {
        failed += 1; // surfaced to the user in the summary toast below
      }
      await new Promise((r) => setTimeout(r, 900)); // small gap so the browser allows multiple saves
    }
    setBulk({ active: false, done: 0, total: 0 });
    if (failed > 0) {
      toast.warning(`Downloaded ${ok} of ${readyVideos.length}. ${failed} failed — please try those again.`);
    } else {
      toast.success(`Downloaded ${ok} ready video${ok === 1 ? "" : "s"}.`);
    }
  };

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Your work</p>
          <h1 className="font-head text-3xl font-bold tracking-tight">Library</h1>
        </div>
        <div className="flex items-center gap-2">
          {readyVideos.length > 0 && (
            <button
              onClick={downloadAll}
              disabled={bulk.active}
              data-testid="library-download-all-btn"
              title="Download every render-ready video at once"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {bulk.active ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />}
              {bulk.active ? `Downloading ${bulk.done}/${bulk.total}…` : `Download all ready (${readyVideos.length})`}
            </button>
          )}
          <Link to="/create" data-testid="library-create-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors">
            <Plus className="h-4 w-4" /> New
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2" data-testid="library-tabs">
        <button
          onClick={() => setShowArchived(false)}
          data-testid="library-tab-active"
          className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${!showArchived ? "bg-blue-600/15 border-blue-500/40 text-blue-300" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}
        >
          Active
        </button>
        <button
          onClick={() => setShowArchived(true)}
          data-testid="library-tab-archived"
          className={`text-sm px-4 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${showArchived ? "bg-blue-600/15 border-blue-500/40 text-blue-300" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}
        >
          <Archive className="h-3.5 w-3.5" /> Archived
        </button>
      </div>

      {showArchived ? (
        archived.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-md p-16 text-center" data-testid="archived-empty">
            <Archive className="h-10 w-10 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Nothing archived — versions you set aside after a comparison land here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archived-grid">
            {archived.map((v) => (
              <div key={v.id} data-testid={`archived-video-${v.id}`} className="border border-white/10 rounded-md bg-[#121212] overflow-hidden opacity-80 hover:opacity-100 transition-opacity flex flex-col">
                <div className="aspect-video bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] grid-noise flex items-center justify-center relative">
                  <Film className="h-8 w-8 text-zinc-700" />
                  <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full text-zinc-400 bg-white/5">archived</span>
                </div>
                <div className="p-4 pb-2">
                  <p className="font-medium truncate">{v.title}</p>
                  <p className="text-xs text-zinc-500 mt-2">{v.platform} · {v.scenes?.length || 0} scenes</p>
                </div>
                <div className="px-4 pb-4 pt-1 mt-auto flex items-center justify-between gap-2">
                  <button
                    onClick={() => restoreVideo(v.id)}
                    data-testid={`restore-video-${v.id}`}
                    className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/10 hover:bg-emerald-600/20 hover:border-emerald-500/40 hover:text-emerald-300 px-3 py-1.5 rounded-md font-semibold transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </button>
                  <Link to={`/video/${v.id}`} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-blue-400 transition-colors">
                    Open <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )
      ) : loading ? (
        <p className="text-zinc-500">Loading…</p>
      ) : videos.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-md p-16 text-center">
          <Film className="h-10 w-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-2">No videos yet.</p>
          <Link to="/create" className="text-blue-400 hover:text-blue-300">Create your first video →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v) => (
            <div key={v.id} data-testid={`library-video-${v.id}`} className="border border-white/10 rounded-md bg-[#121212] overflow-hidden hover:border-white/20 transition-colors group flex flex-col">
              <Link to={`/video/${v.id}`} className="block">
                <div className="aspect-video bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] grid-noise flex items-center justify-center relative">
                  <Film className="h-8 w-8 text-zinc-700" />
                  <span className={`absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[v.status] || "text-zinc-400 bg-white/5"}`}>{v.status}</span>
                  {v.render_status === "processing" ? (
                    <span data-testid={`library-rendering-badge-${v.id}`} className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25 font-medium">
                      <Loader2 className="h-3 w-3 animate-spin" /> Rendering…
                    </span>
                  ) : v.render_ready ? (
                    <span data-testid={`library-ready-badge-${v.id}`} className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 font-medium">
                      <CheckCircle2 className="h-3 w-3" /> Ready
                    </span>
                  ) : null}
                </div>
                <div className="p-4 pb-2">
                  <p className="font-medium truncate">{v.title}</p>
                  <p className="text-xs text-zinc-500 mt-2">{v.platform} · {v.scenes?.length || 0} scenes</p>
                </div>
              </Link>
              <div className="px-4 pb-4 pt-1 mt-auto flex items-center justify-between gap-2">
                <DownloadVideoButton
                  videoId={v.id}
                  renderReady={v.render_ready}
                />
                <Link to={`/video/${v.id}`} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-blue-400 transition-colors" data-testid={`library-open-${v.id}`}>
                  Open <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
              <div className="px-4 pb-4 -mt-1 border-t border-white/5 pt-3">
                <ShareToSocial compact />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
